import { useEffect } from 'react';

/**
 * The keyboard layer.
 *
 * The target is a bookkeeper clearing a hundred receipts without touching the
 * mouse, so this is not a convenience, it is the interface. Everything below
 * follows from one rule: **a shortcut must never fire while someone is
 * typing.** A reviewer correcting a supplier name to "Reece Group" must not
 * have the R approve-adjacent key steal the keystroke, and getting that wrong
 * once destroys trust in every shortcut on the screen.
 */

export interface ReviewKeyboardActions {
  onApprove: () => void;
  onReject: () => void;
  onSaveDraft: () => void;
  onEditFocused: () => void;
  onNextField: () => void;
  onPreviousField: () => void;
  onNextDocument: () => void;
  onPreviousDocument: () => void;
  onCancel: () => void;
  onToggleHelp: () => void;
}

export interface ReviewKeyboardOptions {
  /** False while a field is being edited, or a dialog is open. */
  readonly enabled: boolean;
  readonly actions: ReviewKeyboardActions;
}

/**
 * True when the keystroke belongs to whatever the user is typing into.
 *
 * Checked on the event target rather than tracked in state: a state flag can
 * fall out of step with the DOM after a re-render, and the one time it does is
 * the time a reviewer's keystroke approves a document.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function useReviewKeyboard({ enabled, actions }: ReviewKeyboardOptions): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // Escape is the exception: it is how a reviewer *leaves* an editor, so
      // it has to work from inside one.
      if (event.key === 'Escape') {
        actions.onCancel();
        return;
      }

      if (isTyping(event.target)) return;

      // Never shadow the browser's own shortcuts, Cmd+R must still reload.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      // Tab moves between fields whether or not the shortcut layer is enabled,
      // because that is what Tab does everywhere else and overriding it while
      // "disabled" would be its own surprise.
      if (event.key === 'Tab') {
        event.preventDefault();
        if (event.shiftKey) actions.onPreviousField();
        else actions.onNextField();
        return;
      }

      if (!enabled) return;

      switch (event.key.toLowerCase()) {
        case 'a':
          event.preventDefault();
          actions.onApprove();
          break;
        case 'r':
          event.preventDefault();
          actions.onReject();
          break;
        case 's':
          event.preventDefault();
          actions.onSaveDraft();
          break;
        case 'e':
          event.preventDefault();
          actions.onEditFocused();
          break;
        case 'j':
          event.preventDefault();
          actions.onNextDocument();
          break;
        case 'k':
          event.preventDefault();
          actions.onPreviousDocument();
          break;
        case '?':
          event.preventDefault();
          actions.onToggleHelp();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, actions]);
}
